/**
 * OutstandingInvoices Widget (T052)
 * Pending and overdue invoices with visual indicators
 */

import { FileText, AlertTriangle } from 'lucide-react';
import type { DashboardData } from '../../hooks/useDashboard';

interface Props {
  data: DashboardData['invoices'];
}

export default function OutstandingInvoices({ data }: Props) {
  const hasInvoices = data.pending.list.length > 0 || data.overdue.list.length > 0;

  return (
    <div className="claude-card overflow-hidden">
      <div className="px-6 py-5 border-b border-border">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold tracking-tight">Outstanding Invoices</h2>
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{data.pending.count} pending</span>
            {data.overdue.count > 0 && (
              <span className="claude-badge claude-badge-error">{data.overdue.count} overdue</span>
            )}
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Total outstanding: {data.pending.totalFormatted}
        </p>
      </div>

      <div className="divide-y divide-border">
        {!hasInvoices ? (
          <div className="claude-empty-state">
            <FileText className="claude-empty-state-icon" />
            <h3 className="claude-empty-state-title">All caught up!</h3>
            <p className="claude-empty-state-description">No outstanding invoices at this time.</p>
          </div>
        ) : (
          <>
            {/* Overdue first */}
            {data.overdue.list.map((invoice) => (
              <div
                key={invoice.id}
                className="px-6 py-4 hover:bg-muted/30 transition-colors cursor-pointer bg-destructive/5"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-destructive" />
                      <p className="font-semibold text-foreground">{invoice.invoiceNumber}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">{invoice.customerName}</p>
                  </div>
                  <div className="text-right space-y-1 flex-shrink-0">
                    <p className="font-semibold text-destructive">{invoice.totalFormatted}</p>
                    <p className="text-xs text-destructive">{invoice.daysPastDue} days overdue</p>
                  </div>
                </div>
              </div>
            ))}
            {/* Pending */}
            {data.pending.list
              .filter((inv) => !inv.isOverdue)
              .map((invoice) => (
                <div
                  key={invoice.id}
                  className="px-6 py-4 hover:bg-muted/30 transition-colors cursor-pointer"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0 space-y-1">
                      <p className="font-semibold text-foreground">{invoice.invoiceNumber}</p>
                      <p className="text-sm text-muted-foreground">{invoice.customerName}</p>
                    </div>
                    <div className="text-right space-y-1 flex-shrink-0">
                      <p className="font-semibold">{invoice.totalFormatted}</p>
                      <p className="text-xs text-muted-foreground">
                        Due{' '}
                        {new Date(invoice.dueDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                        })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
          </>
        )}
      </div>
    </div>
  );
}
